import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  Check,
  Code,
  Copy,
  Download,
  FileImage,
  FileText,
  Loader2,
  Mail,
  QrCode,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import {
  ActivationPackWithTarget,
  ASSET_TYPE_INFO,
  GeneratedAsset,
  GeneratedAssetType,
} from '@/types/activation/activation-pack';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAssetGenerator, useUpdateActivationPack } from '@/hooks/activation';
import { useGenieProducts } from '@/hooks/marketplace/useGenieProducts';
import {
  getExpectedActivationAssetTypes,
  inspectActivationAssetCompleteness,
} from '@/services/activation/zip-bundler';

interface PackAssetsSectionProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

const ASSET_ICONS: Record<GeneratedAssetType, typeof QrCode> = {
  qr_png: QrCode,
  qr_svg: QrCode,
  product_qr_png: QrCode,
  product_qr_svg: QrCode,
  sticker_pdf: FileText,
  email_signature_html: Mail,
  social_card_png: FileImage,
  embed_code: Code,
  lookbook_pdf: BookOpen,
  app_poster_png: FileImage,
  app_poster_pdf: FileText,
  poster_pack_pdf: FileText,
};

const isTextAsset = (type: GeneratedAssetType) =>
  type === 'email_signature_html' || type === 'embed_code';

const isImagePreviewable = (type: GeneratedAssetType) =>
  type === 'qr_png' || type === 'social_card_png' || type === 'qr_svg' ||
  type === 'product_qr_png' || type === 'product_qr_svg';

/** Build a previewable URL for image assets. URL-backed assets pass through. */
const buildPreviewUrl = (asset: GeneratedAsset): string | null => {
  try {
    if (asset.data instanceof Blob) {
      return URL.createObjectURL(asset.data);
    }
    if (typeof asset.data === 'string') {
      if (asset.data.startsWith('data:')) return asset.data;
      if (/^https?:\/\//i.test(asset.data)) return asset.data;
      if (asset.type === 'qr_svg' || asset.type === 'product_qr_svg') {
        const blob = new Blob([asset.data], { type: asset.mimeType });
        return URL.createObjectURL(blob);
      }
    }
    return null;
  } catch {
    return null;
  }
};

const textValue = (asset: GeneratedAsset): string =>
  typeof asset.data === 'string' ? asset.data : '';

const getCacheIdentity = (pack: ActivationPackWithTarget): string =>
  JSON.stringify(
    Object.entries(pack.generated_assets || {})
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([type, entry]) => [
        type,
        entry?.generated_at,
        entry?.url,
        entry?.data,
        entry?.filename,
        entry?.mimeType,
      ]),
  );

const buildPreviewUrls = (assets: GeneratedAsset[]): Record<string, string> =>
  assets.reduce<Record<string, string>>((urls, asset) => {
    if (!isImagePreviewable(asset.type)) return urls;
    const url = buildPreviewUrl(asset);
    if (url) urls[asset.type] = url;
    return urls;
  }, {});

const revokePreviewUrls = (urls: Record<string, string>): void => {
  Object.values(urls).forEach((url) => {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  });
};

const getExpectedCachedTypes = (pack: ActivationPackWithTarget): GeneratedAssetType[] =>
  getExpectedActivationAssetTypes(pack.touchpoints, pack.asset_config);

const NO_PRODUCT = '__none__';

export const PackAssetsSection = ({
  pack,
  brand = '#4E9CFF',
}: PackAssetsSectionProps) => {
  const {
    downloadAllAssets,
    error,
    generateAllAssets,
    getCachedAssets,
    isGenerating,
    persistAssets,
    resolveAsset,
  } = useAssetGenerator(pack);
  // Manual 2.0: a marketplace genie's pack can carry one product's manual QR.
  const genieId = pack.agent_id ?? pack.agent?.id ?? null;
  const { data: products = [] } = useGenieProducts(genieId);
  const { mutate: updatePack, isPending: isUpdatingProduct } = useUpdateActivationPack();
  const handleProductChange = (slug: string) => {
    const product = products.find((item) => item.slug === slug) ?? null;
    updatePack({
      packId: pack.id,
      updates: {
        asset_config: {
          ...pack.asset_config,
          product_slug: product?.slug ?? null,
          product_name: product?.name ?? null,
        },
      },
    });
  };
  const [assets, setAssets] = useState<GeneratedAsset[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [copiedType, setCopiedType] = useState<GeneratedAssetType | null>(null);
  const [isZipping, setIsZipping] = useState(false);
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false);
  const [preparationError, setPreparationError] = useState<string | null>(null);
  const [shouldRetryDownload, setShouldRetryDownload] = useState(false);
  const autoFiredPackIdRef = useRef<string | null>(null);
  const cacheIdentity = getCacheIdentity(pack);
  const expectedCachedTypes = useMemo(
    () => getExpectedCachedTypes(pack),
    [pack],
  );

  const cachedAssets = useMemo<GeneratedAsset[]>(
    () => {
      // Keep hydration keyed to cache content, not only the surrounding pack object.
      void cacheIdentity;
      return getCachedAssets();
    },
    [cacheIdentity, getCachedAssets],
  );

  const hasCompleteCachedAssets = useMemo(() => {
    return inspectActivationAssetCompleteness(
      cachedAssets,
      expectedCachedTypes,
    ).complete;
  }, [cachedAssets, expectedCachedTypes]);

  const prepareAssets = useCallback(async () => {
    setPreparationError(null);
    setShouldRetryDownload(false);
    setHasAttemptedGeneration(false);

    const result = await generateAllAssets();
    const completeness = inspectActivationAssetCompleteness(
      result,
      expectedCachedTypes,
    );
    setAssets(result);
    setPreviewUrls(buildPreviewUrls(result));
    setHasAttemptedGeneration(true);

    if (!completeness.complete) {
      const missingLabels = completeness.missing.map(
        (type) => ASSET_TYPE_INFO[type].label,
      );
      setPreparationError(
        result.length > 0
          ? `Only ${completeness.included.length} of ${completeness.expected.length} preview assets are ready. Missing: ${missingLabels.join(', ')}.`
          : 'No preview assets could be prepared. Nothing partial was saved.',
      );
      return;
    }

    try {
      await persistAssets(result);
    } catch (persistError) {
      console.error('Failed to persist generated activation assets:', persistError);
      setPreparationError(
        'The assets are ready for this visit, but they could not be saved. Retry to keep them available next time.',
      );
    }
  }, [expectedCachedTypes, generateAllAssets, persistAssets]);

  useEffect(() => {
    if (hasCompleteCachedAssets) {
      setAssets(cachedAssets);
      setPreviewUrls(buildPreviewUrls(cachedAssets));
      setHasAttemptedGeneration(true);
      setPreparationError(null);
      autoFiredPackIdRef.current = null;
      return;
    }

    setAssets(cachedAssets);
    setPreviewUrls(buildPreviewUrls(cachedAssets));
    setHasAttemptedGeneration(false);
    if (autoFiredPackIdRef.current === pack.id) return;
    autoFiredPackIdRef.current = pack.id;
    void prepareAssets();
  }, [
    cacheIdentity,
    cachedAssets,
    hasCompleteCachedAssets,
    pack.id,
    prepareAssets,
  ]);

  useEffect(
    () => () => revokePreviewUrls(previewUrls),
    [previewUrls],
  );

  const handleCopy = async (asset: GeneratedAsset) => {
    try {
      // Inline HTML is cheap to rebuild and older caches may contain a stale
      // hostname or an earlier signature template. Resolve it at click time.
      const currentAsset = await resolveAsset(asset.type);
      if (!currentAsset) throw new Error('Asset is not ready');
      await navigator.clipboard.writeText(textValue(currentAsset));
      setCopiedType(asset.type);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedType(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDownloadEverything = async () => {
    if (isZipping || isGenerating) return;
    setIsZipping(true);
    setShouldRetryDownload(false);
    try {
      const completeAssets = await downloadAllAssets();
      setAssets(completeAssets);
      setPreviewUrls(buildPreviewUrls(completeAssets));
      setHasAttemptedGeneration(true);
      setPreparationError(null);
      setShouldRetryDownload(false);
      toast.success('Downloaded your activation pack');
    } catch (err) {
      console.error('Failed to build zip:', err);
      setShouldRetryDownload(true);
      toast.error('Couldn\'t build a complete activation pack', {
        description:
          err instanceof Error
            ? err.message
            : 'Please try again. No partial bundle was downloaded.',
      });
    } finally {
      setIsZipping(false);
    }
  };

  const handleRetryPreparation = () => {
    if (isGenerating || isZipping) return;
    if (shouldRetryDownload) {
      void handleDownloadEverything();
      return;
    }
    autoFiredPackIdRef.current = pack.id;
    void prepareAssets();
  };

  const renderAssetRow = (asset: GeneratedAsset) => {
    const Icon = ASSET_ICONS[asset.type];
    const previewUrl = previewUrls[asset.type];
    const isCopied = copiedType === asset.type;

    return (
      <div
        key={asset.type}
        className="flex items-center gap-4 py-3"
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${brand}15` }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={asset.label}
              className="w-9 h-9 object-contain rounded-md"
            />
          ) : (
            <Icon className="w-5 h-5" style={{ color: brand }} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {asset.label}
          </div>
          <div className="text-xs text-gray-500 truncate">{asset.filename}</div>
        </div>
        {isTextAsset(asset.type) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCopy(asset)}
            className="h-8 shrink-0"
          >
            {isCopied ? (
              <>
                <Check className="w-4 h-4 mr-1.5 text-genie-primary" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1.5" />
                Copy
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  const assetCompleteness = useMemo(
    () => inspectActivationAssetCompleteness(assets, expectedCachedTypes),
    [assets, expectedCachedTypes],
  );
  const isReady = assetCompleteness.complete;
  const isPartial = assets.length > 0 && !isReady;
  const isPreparing =
    isGenerating || (!hasAttemptedGeneration && !hasCompleteCachedAssets);
  const visibleError = preparationError || error;

  return (
    <OverviewSectionCard
      title="Your Pack"
      subtitle="Everything you need to put your Genie in front of customers — one click."
      genieIcon="clapperboard"
      brand={brand}
    >
      <div className="space-y-5">
        {/* Download Everything — the only action that matters */}
        <div className="rounded-2xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
          <div className="rounded-[14px] bg-white px-5 py-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-900">
                Download everything
              </div>
              <div className="text-xs text-gray-600 mt-0.5">
                One .zip with production posters, an app poster, a placement lookbook, QR codes,
                every enabled sticker/signature/social asset, an embed snippet,
                ready-to-paste launch copy, and a start-here guide.
              </div>
            </div>
            <Button
              onClick={handleDownloadEverything}
              disabled={isZipping || isGenerating}
              className="text-white border-0 shrink-0 px-5 py-2.5 h-auto"
              style={{
                background: `linear-gradient(135deg, ${brand}, #FF4DB8)`,
              }}
            >
              {isZipping || isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Building…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download .zip
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Product manual QR — only a genie with catalogue products sees this */}
        {products.length > 0 && (
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3" data-testid="pack-product-qr">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Product manual QR</div>
                <div className="text-xs text-gray-500">
                  Pick a product and the pack adds a QR that opens its manual page, with answers cited from the official manual.
                </div>
              </div>
              <Select
                value={pack.asset_config.product_slug || NO_PRODUCT}
                onValueChange={(value) => handleProductChange(value === NO_PRODUCT ? '' : value)}
                disabled={isUpdatingProduct}
              >
                <SelectTrigger className="w-full sm:w-72 border-gray-300 h-auto py-2">
                  <SelectValue placeholder="No product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PRODUCT}>No product QR</SelectItem>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.slug}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* What's inside */}
        <div>
          <div className="mb-1 flex items-center justify-between gap-3 px-1">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              What's inside
            </h4>
            <span
              className={`text-xs font-semibold ${
                isReady
                  ? 'text-genie-primary'
                  : isPartial || visibleError
                    ? 'text-red-600'
                    : 'text-gray-500'
              }`}
            >
              {isReady
                ? `Ready · ${assetCompleteness.included.length} assets`
                : isPartial
                  ? `Incomplete · ${assetCompleteness.included.length}/${assetCompleteness.expected.length}`
                  : isPreparing
                    ? 'Preparing'
                    : 'Not ready'}
            </span>
          </div>
          {visibleError && (
            <div
              role="alert"
              className="mb-3 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div>
                  <p className="text-sm font-semibold text-red-600">
                    {shouldRetryDownload
                      ? "The complete pack download couldn't finish."
                      : isReady && preparationError
                      ? "Your preview pack is ready, but it wasn't saved."
                      : "Your preview pack isn't complete yet."}
                  </p>
                  <p className="mt-0.5 text-xs text-red-600 break-words">
                    {visibleError}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isPreparing || isZipping}
                onClick={handleRetryPreparation}
                className="shrink-0"
              >
                {isPreparing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Retry
              </Button>
            </div>
          )}
          <div className="rounded-xl border border-gray-200 bg-white px-4 divide-y divide-gray-100">
            {assets.length > 0 ? (
              assets.map(renderAssetRow)
            ) : isPreparing ? (
              <div className="flex items-center gap-2 py-6 text-sm text-gray-500 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: brand }} />
                <span>Putting your pack together…</span>
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-gray-500">
                No assets are ready yet. Use Retry above to prepare the pack
                again.
              </div>
            )}
          </div>
          {isPartial && (
            <p className="mt-2 px-1 text-xs text-red-600">
              Missing:{' '}
              {assetCompleteness.missing
                .map((type) => ASSET_TYPE_INFO[type].label)
                .join(', ')}
              . This partial result was not saved as ready.
            </p>
          )}
        </div>
      </div>
    </OverviewSectionCard>
  );
};
